import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { MVTLayer } from '@deck.gl/geo-layers';
import { GeoJsonLayer } from '@deck.gl/layers';
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import layers from '@rsos/components/capstone/common/Map/useDeckGL/storeLayers';
import {
  HIGHLIGHT_COLOR,
  SELECTED_ROOM_HIGHLIGHT,
  SELECTED_ROOM_OUTLINE,
  HOVER_ROOM_HIGHLIGHT,
  FOOTPRINT_OUTLINE_COLOR,
  FOOTPRINT_FILL_COLOR,
  GEOJSON_FOOTPRINT_FILL,
  ROOM_OUTLINE_COLOR,
  CALL_FLOOR_HIGHLIGHT_COLOR,
  CALL_FLOOR_HIGHLIGHT_WIDTH,
  OUTDOOR_POINT_LAYER_NAME,
  INDOOR_POINT_MARKER_COLOR,
} from '../../../Map/useDeckGL/PureWireless/constants';
import {
  PURE_WIRELESS_URL,
  getRoomFillColor,
  getFeatureLabel,
  getPolygonCentroid,
  getBuildingKey,
} from '../../../Map/useDeckGL/PureWireless/helpers';
import type {
  DeckPickingInfo,
  EsriMapInstance,
  EsriHitTestResponse,
  OverlayType,
  TileFeature,
  TileProperties,
  PolygonFeature,
  MVTTile,
} from '../../../Map/useDeckGL/PureWireless/types';
import { createOutdoorPointsMVTLayer } from '../../../Map/useDeckGL/PureWireless/usePureWirelessCore';
import { usePureWirelessESRI } from './usePureWirelessESRI';

/**
 * Minimum zoom levels at which labels become visible (ESRI minScale values).
 * Derived from web-mercator: scale ≈ 591657550.5 / 2^zoom.
 * Higher zoom = smaller scale number = more zoomed in.
 */
const ROOM_LABEL_MIN_SCALE = 1200; // ≈ zoom 19
const POINT_LABEL_MIN_SCALE = 150; // ≈ zoom 23

const TOOLTIP_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 1000,
  pointerEvents: 'none',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  padding: '8px 12px',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
  whiteSpace: 'nowrap',
  margin: '-10px',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Convert deck.gl RGBA [0-255 alpha] → ESRI RGBA [0-1 alpha] */
const toEsriColor = (
  rgba: [number, number, number, number],
): [number, number, number, number] => [
  rgba[0],
  rgba[1],
  rgba[2],
  rgba[3] / 255,
];

/** Extract ESRI-compatible rings from a GeoJSON Polygon / MultiPolygon */
function getRings(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): number[][][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return geometry.coordinates;
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat();
  return [];
}

/**
 * ESRI autocast helpers – the ArcGIS JS API accepts plain objects with a
 * `type` discriminator at runtime, but the TypeScript definitions expect
 * class instances.  These tiny wrappers cast through `unknown` so the rest
 * of the code stays free of `any`.
 */
function esriPolygon(rings: number[][][]): __esri.GeometryProperties {
  return {
    type: 'polygon',
    rings,
    spatialReference: { wkid: 4326 },
  } as unknown as __esri.GeometryProperties;
}

function esriPoint(
  longitude: number,
  latitude: number,
): __esri.GeometryProperties {
  return {
    type: 'point',
    longitude,
    latitude,
  } as unknown as __esri.GeometryProperties;
}

function esriFill(
  color: [number, number, number, number],
  outline?: { color: [number, number, number, number]; width: number },
): __esri.SymbolProperties {
  return {
    type: 'simple-fill',
    color,
    outline: outline
      ? { type: 'simple-line', color: outline.color, width: outline.width }
      : { type: 'simple-line', color: [0, 0, 0, 0], width: 0 },
  } as unknown as __esri.SymbolProperties;
}

function esriText(
  text: string,
  color: [number, number, number, number],
  options: {
    haloColor: number[];
    haloSize: number;
    xoffset?: number;
    yoffset?: number;
    horizontalAlignment?: string;
    fontSize?: number;
  },
): __esri.SymbolProperties {
  return {
    type: 'text',
    text,
    color,
    font: {
      size: options.fontSize ?? 12,
      family: 'Arial',
      weight: 'bold',
    },
    haloColor: options.haloColor,
    haloSize: options.haloSize,
    ...(options.xoffset != null && { xoffset: options.xoffset }),
    ...(options.yoffset != null && { yoffset: options.yoffset }),
    ...(options.horizontalAlignment && {
      horizontalAlignment: options.horizontalAlignment,
    }),
  } as unknown as __esri.SymbolProperties;
}

function esriTriangleMarker(
  color: [number, number, number, number],
  size: number,
): __esri.SymbolProperties {
  return {
    type: 'simple-marker',
    style: 'triangle',
    color,
    size,
    outline: { type: 'simple-line', color: [0, 0, 0, 0], width: 0 },
  } as unknown as __esri.SymbolProperties;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface PureWirelessESRIProps {
  overlay: OverlayType;
  map: EsriMapInstance;
}

// ─── Component ───────────────────────────────────────────────────────────────

const PureWirelessDeck = React.memo(
  ({ overlay, map }: PureWirelessESRIProps) => {
    const {
      isDetailMode,
      hoverInfo,
      selectedFloor,
      isVisible,
      geoJSONData,
      isLoadingGeoJSON,
      selectedRoomFeature,
      buildingFloorsRef,
      isDarkMode,
      textColor,
      callFloorHighlightColor,
      shouldHighlightFloor,
      handleClickRef,
      handleRoomClickRef,
      handleHoverRef,
    } = usePureWirelessESRI(map);

    // ─── Effect 1: MVT layer via shared overlay / storeLayers ─────────────
    // The MVT layer renders building footprint outlines using the existing
    // DeckOverlay that all other deck.gl layers share.  No separate DeckLayer.
    useEffect(() => {
      if (!overlay) return;

      const showGeoJSON = isDetailMode && geoJSONData && !isLoadingGeoJSON;

      if (!isVisible) {
        layers.deleteLayer('PureWireless');
        layers.deleteLayer('pure-wireless-outdoor-points-dark');
        layers.deleteLayer('pure-wireless-outdoor-points-light');
        overlay.setLayers([...layers.getAll()]);
        return;
      }

      // Create MVT layer inline (id set in constructor → no mismatch)
      const mvtLayer = new MVTLayer<TileProperties>({
        id: 'PureWireless',
        data: PURE_WIRELESS_URL,
        minZoom: 14,
        maxZoom: 22,
        binary: false,
        visible: !showGeoJSON, // hidden when GeoJSON detail is active

        renderSubLayers: (props: { data?: TileFeature[] }) => {
          if (props.data && Array.isArray(props.data)) {
            const filtered = props.data.filter(
              (f: TileFeature) =>
                f.geometry?.type !== 'Point' &&
                f.properties?.layerName !== OUTDOOR_POINT_LAYER_NAME,
            );
            return new GeoJsonLayer({ ...props, data: filtered });
          }
          return new GeoJsonLayer(props);
        },

        getFillColor: (f: TileFeature) => {
          if (f.properties?.space_type?.toLowerCase() === 'footprint')
            return FOOTPRINT_FILL_COLOR;
          return [0, 0, 0, 0] as [number, number, number, number];
        },
        getLineColor: (f: TileFeature) => {
          if (f.properties?.space_type?.toLowerCase() === 'footprint')
            return FOOTPRINT_OUTLINE_COLOR;
          return [0, 0, 0, 0] as [number, number, number, number];
        },
        getLineWidth: 2,
        lineWidthMinPixels: 2,
        getPointRadius: 0,
        pointRadiusMinPixels: 0,
        pickable: true,
        autoHighlight: true,
        highlightColor: HIGHLIGHT_COLOR,

        onHover: (info: unknown) =>
          handleHoverRef.current(info as DeckPickingInfo),

        onClick: (info: unknown) => {
          const pickInfo = info as DeckPickingInfo;
          if (pickInfo.object) {
            const p = pickInfo.object.properties;
            if (p && p.floor_level !== undefined) {
              const bKey = getBuildingKey(p);
              if (!buildingFloorsRef.current.has(bKey))
                buildingFloorsRef.current.set(bKey, new Map());
              const fm = buildingFloorsRef.current.get(bKey);
              if (fm && !fm.has(p.floor_level))
                fm.set(p.floor_level, {
                  floor_level: p.floor_level,
                  floor_label: p.floor_label || `Floor ${p.floor_level}`,
                });
            }
            return handleClickRef.current(pickInfo);
          }
          return false;
        },

        onTileLoad: (tile: MVTTile) => {
          if (tile.content && Array.isArray(tile.content)) {
            tile.content.forEach((f: TileFeature) => {
              const p = f.properties;
              if (p && p.floor_level !== undefined) {
                const bKey = getBuildingKey(p);
                if (!buildingFloorsRef.current.has(bKey))
                  buildingFloorsRef.current.set(bKey, new Map());
                const fm = buildingFloorsRef.current.get(bKey);
                if (fm && !fm.has(p.floor_level))
                  fm.set(p.floor_level, {
                    floor_level: p.floor_level,
                    floor_label: p.floor_label || `Floor ${p.floor_level}`,
                  });
              }
            });
          }
        },
      } as unknown as Record<string, unknown>);

      layers.save(mvtLayer);

      // Remove stale outdoor layer from the opposite theme before saving new one
      const themeKey = isDarkMode ? 'dark' : 'light';
      const staleKey = isDarkMode ? 'light' : 'dark';
      layers.deleteLayer(`pure-wireless-outdoor-points-${staleKey}`);

      const outdoorPointsLayer = createOutdoorPointsMVTLayer({
        textColor,
        themeKey,
      });
      layers.save(outdoorPointsLayer);

      overlay.setLayers([...layers.getAll()]);

      return () => {
        layers.deleteLayer('PureWireless');
        layers.deleteLayer('pure-wireless-outdoor-points-dark');
        layers.deleteLayer('pure-wireless-outdoor-points-light');
        try {
          overlay.setLayers([...layers.getAll()]);
        } catch {
          // Overlay may be destroyed during map-provider switch
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      overlay,
      isVisible,
      isDetailMode,
      geoJSONData,
      isLoadingGeoJSON,
      buildingFloorsRef,
      textColor,
    ]);

    // ─── Effect 2: GeoJSON detail via ESRI native GraphicsLayer ───────────
    // Renders room polygons, footprint, and labels using ESRI's own renderer
    // so we avoid all deck.gl compositing / ordering issues on the shared
    // DeckLayer.  The GraphicsLayer is a plain ESRI layer — not a DeckLayer.
    useEffect(() => {
      const esriMap = map?.mapInstance?.map;
      const view = map?.mapInstance?.view;
      if (!esriMap || !view) return;

      const showGeoJSON =
        isVisible && isDetailMode && geoJSONData && !isLoadingGeoJSON;
      if (!showGeoJSON) return;

      // ── helpers ──────────────────────────────────────────────────────────
      const filterByFloor = (
        features: GeoJSON.Feature[] | undefined,
      ): GeoJSON.Feature[] => {
        if (!features) return [];
        if (selectedFloor === null) return features;
        return features.filter(
          f => f.properties && f.properties.floor_level === selectedFloor,
        );
      };

      const graphicsLayer = new GraphicsLayer({
        id: 'PureWirelessGeoJSON',
      });
      const allGraphics: InstanceType<typeof Graphic>[] = [];
      // Label graphics — each group gets its own zoom-gated layer
      const roomLabelGraphics: InstanceType<typeof Graphic>[] = [];
      const pointLabelGraphics: InstanceType<typeof Graphic>[] = [];

      // ── 1. Footprint (gray building outline background) ─────────────────
      if (geoJSONData.footprint?.features) {
        const footprintFeatures = filterByFloor(geoJSONData.footprint.features);
        footprintFeatures.forEach(feature => {
          const rings = getRings(
            feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
          );
          if (rings.length > 0) {
            allGraphics.push(
              new Graphic({
                geometry: esriPolygon(rings),
                symbol: esriFill(toEsriColor(GEOJSON_FOOTPRINT_FILL)),
              }),
            );
          }
        });

        // Call-floor highlight outline
        if (shouldHighlightFloor) {
          const hlColor = callFloorHighlightColor ?? CALL_FLOOR_HIGHLIGHT_COLOR;
          footprintFeatures.forEach(feature => {
            const rings = getRings(
              feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
            );
            if (rings.length > 0) {
              allGraphics.push(
                new Graphic({
                  geometry: esriPolygon(rings),
                  symbol: esriFill([0, 0, 0, 0], {
                    color: toEsriColor(hlColor),
                    width: CALL_FLOOR_HIGHLIGHT_WIDTH,
                  }),
                }),
              );
            }
          });
        }
      }

      // ── 2. Room polygons (coloured by room type) ────────────────────────
      if (geoJSONData.polygon?.features) {
        const polygonFeatures = filterByFloor(geoJSONData.polygon.features);
        polygonFeatures.forEach(feature => {
          const rings = getRings(
            feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
          );
          if (rings.length > 0) {
            const fillColor = getRoomFillColor(feature as TileFeature);
            allGraphics.push(
              new Graphic({
                geometry: esriPolygon(rings),
                symbol: esriFill(toEsriColor(fillColor), {
                  color: toEsriColor(ROOM_OUTLINE_COLOR),
                  width: 1,
                }),
                // Stash the original GeoJSON feature so click handler can use it
                attributes: { _feature: feature },
              }),
            );
          }
        });

        // ── 3. Labels (zoom-gated — only visible at zoom >= 19) ────────────
        const labelFeatures = polygonFeatures.filter(f =>
          getFeatureLabel(f as TileFeature),
        );
        labelFeatures.forEach(feature => {
          const centroid = getPolygonCentroid(feature as PolygonFeature);
          const label = getFeatureLabel(feature as TileFeature);
          if (centroid && label) {
            roomLabelGraphics.push(
              new Graphic({
                geometry: esriPoint(centroid[0], centroid[1]),
                symbol: esriText(label, toEsriColor(textColor), {
                  haloColor: isDarkMode ? [0, 0, 0, 0.7] : [255, 255, 255, 0.7],
                  haloSize: 1,
                }),
              }),
            );
          }
        });
      }

      // ── 4. Points of interest (muted-green triangles + labels) ──────────
      // Triangle markers go in a separate layer so they render above the
      // selected-room highlight (which Effect 3 inserts at index 1).
      const pointMarkerGraphics: InstanceType<typeof Graphic>[] = [];

      if (geoJSONData.point?.features) {
        const pointFeatures = filterByFloor(geoJSONData.point.features);
        pointFeatures.forEach(feature => {
          const geom = (feature as GeoJSON.Feature<GeoJSON.Point>).geometry;
          if (geom?.coordinates) {
            const [lng, lat] = geom.coordinates;
            // Triangle marker
            pointMarkerGraphics.push(
              new Graphic({
                geometry: esriPoint(lng, lat),
                symbol: esriTriangleMarker(INDOOR_POINT_MARKER_COLOR, 6),
                attributes: { _feature: feature },
              }),
            );
            // Label below the point (zoom-gated)
            const label = getFeatureLabel(feature as TileFeature);
            if (label) {
              pointLabelGraphics.push(
                new Graphic({
                  geometry: esriPoint(lng, lat),
                  symbol: esriText(label, toEsriColor(textColor), {
                    haloColor: isDarkMode
                      ? [0, 0, 0, 0.7]
                      : [255, 255, 255, 0.7],
                    haloSize: 1,
                    yoffset: -10,
                    horizontalAlignment: 'center',
                    fontSize: 10,
                  }),
                }),
              );
            }
          }
        });
      }

      graphicsLayer.addMany(allGraphics);
      // Index 0: main graphics (footprints + room polygons)
      esriMap.add(graphicsLayer, 0);
      // Index 1 is reserved for the selected-room highlight (Effect 3)

      // Index 2: point markers (above highlight so they stay visible)
      // Hidden until room labels are visible so they don't clutter the map
      let pointMarkerLayer: GraphicsLayer | null = null;
      if (pointMarkerGraphics.length > 0) {
        pointMarkerLayer = new GraphicsLayer({
          id: 'PureWirelessPointMarkers',
          minScale: ROOM_LABEL_MIN_SCALE,
        });
        pointMarkerLayer.addMany(pointMarkerGraphics);
        esriMap.add(pointMarkerLayer, 2);
      }

      // Index 3: room labels (zoom-gated)
      let roomLabelLayer: GraphicsLayer | null = null;
      if (roomLabelGraphics.length > 0) {
        roomLabelLayer = new GraphicsLayer({
          id: 'PureWirelessRoomLabels',
          minScale: ROOM_LABEL_MIN_SCALE,
        });
        roomLabelLayer.addMany(roomLabelGraphics);
        esriMap.add(roomLabelLayer, 3);
      }

      // Index 4: point labels (zoom-gated, requires more zoom)
      let pointLabelLayer: GraphicsLayer | null = null;
      if (pointLabelGraphics.length > 0) {
        pointLabelLayer = new GraphicsLayer({
          id: 'PureWirelessPointLabels',
          minScale: POINT_LABEL_MIN_SCALE,
        });
        pointLabelLayer.addMany(pointLabelGraphics);
        esriMap.add(pointLabelLayer, 4);
      }

      // ── Click handler for room polygons ──────────────────────────────────
      let clickHandle: { remove: () => void } | null = null;
      if (view.on && view.hitTest) {
        const hitTest = view.hitTest.bind(view);
        clickHandle = view.on('click', (event: unknown) => {
          hitTest(event, { include: [graphicsLayer] }).then(
            (response: EsriHitTestResponse) => {
              const results = response?.results;
              if (results && results.length > 0) {
                const graphic = results[0]?.graphic;
                const feature = graphic?.attributes?._feature as
                  | TileFeature
                  | undefined;
                if (feature) {
                  handleRoomClickRef.current({
                    object: feature,
                  } as DeckPickingInfo);
                }
              }
            },
          );
        });
      }

      // ── Hover highlight for room polygons ────────────────────────────────
      const hoverLayer = new GraphicsLayer({ id: 'PureWirelessHoverRoom' });
      esriMap.add(hoverLayer, 1); // between main graphics (0) and point markers (2+)

      let hoverHandle: { remove: () => void } | null = null;
      if (view.on && view.hitTest) {
        const hitTest = view.hitTest.bind(view);
        hoverHandle = view.on('pointer-move', (event: unknown) => {
          hitTest(event, { include: [graphicsLayer] }).then(
            (response: EsriHitTestResponse) => {
              hoverLayer.removeAll();
              const results = response?.results;
              if (results && results.length > 0) {
                const graphic = results[0]?.graphic;
                const feature = graphic?.attributes?._feature as
                  | GeoJSON.Feature
                  | undefined;
                const geom = feature?.geometry as
                  | GeoJSON.Polygon
                  | GeoJSON.MultiPolygon
                  | undefined;
                if (geom) {
                  const rings = getRings(geom);
                  if (rings.length > 0) {
                    hoverLayer.add(
                      new Graphic({
                        geometry: esriPolygon(rings),
                        symbol: esriFill(HOVER_ROOM_HIGHLIGHT),
                      }),
                    );
                  }
                }
              }
            },
          );
        });
      }

      return () => {
        if (clickHandle) clickHandle.remove();
        if (hoverHandle) hoverHandle.remove();
        try {
          esriMap.remove(hoverLayer);
        } catch {
          /* already removed */
        }
        try {
          esriMap.remove(graphicsLayer);
        } catch {
          /* already removed */
        }
        if (pointMarkerLayer) {
          try {
            esriMap.remove(pointMarkerLayer);
          } catch {
            /* already removed */
          }
        }
        if (roomLabelLayer) {
          try {
            esriMap.remove(roomLabelLayer);
          } catch {
            /* already removed */
          }
        }
        if (pointLabelLayer) {
          try {
            esriMap.remove(pointLabelLayer);
          } catch {
            /* already removed */
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      map,
      isDetailMode,
      isVisible,
      geoJSONData,
      isLoadingGeoJSON,
      selectedFloor,
      shouldHighlightFloor,
      callFloorHighlightColor,
      textColor,
      isDarkMode,
    ]);

    // ─── Effect 3: Selected room highlight ──────────────────────────────────
    useEffect(() => {
      const esriMap = map?.mapInstance?.map;
      if (!esriMap) return;

      if (!selectedRoomFeature) return;

      const geom = selectedRoomFeature.geometry as
        | GeoJSON.Polygon
        | GeoJSON.MultiPolygon
        | undefined;
      if (!geom) return;

      const rings = getRings(geom);
      if (rings.length === 0) return;

      const highlightLayer = new GraphicsLayer({
        id: 'PureWirelessSelectedRoom',
      });
      highlightLayer.add(
        new Graphic({
          geometry: esriPolygon(rings),
          symbol: esriFill(SELECTED_ROOM_HIGHLIGHT, {
            color: SELECTED_ROOM_OUTLINE,
            width: 2,
          }),
        }),
      );
      // Insert at index 1 (above room polygons at 0, below point markers at 2+)
      esriMap.add(highlightLayer, 1);

      return () => {
        try {
          esriMap.remove(highlightLayer);
        } catch {
          /* already removed */
        }
      };
    }, [map, selectedRoomFeature]);

    // ─── Tooltip ──────────────────────────────────────────────────────────

    const renderTooltip = () => {
      if (!hoverInfo) return null;
      return createPortal(
        <div
          style={{
            ...TOOLTIP_STYLE,
            left: hoverInfo.x,
            top: hoverInfo.y,
            transform: 'translate(20%, 50%)',
          }}
        >
          View Indoor maps
        </div>,
        document.body,
      );
    };

    return renderTooltip();
  },
);

export default PureWirelessDeck;
